import type { CloudImageSize } from '../../types/cloud-recordings';
import type { CameraThumbnailStatus } from '../../types/camera-status';
import type { ThumbnailResolver, ThumbnailSource } from './types/thumbnail';
import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';
import { deriveImageKey } from './recording-key';
import { hasPreservedEraKey, resolveMasterKey } from './master-key-management';
import { getCameraThumbnail, watchCameraThumbnail } from '../camera-thumbnail-watch';
import { camera_storage_passphrases } from '../../stores/camera_storage_passphrases';
import { camera_storage_resolved_keys } from '../../stores/camera_storage_resolved_keys';

/** AES-GCM nonce length in bytes (matches the firmware's `GCM_NONCE_BYTES`). */
const GCM_NONCE_BYTES = 12;

/** AES-GCM authentication tag length in bits (16-byte tag appended to the ciphertext). */
const GCM_TAG_LENGTH_BITS = 128;

/**
 * Builds the 12-byte AES-GCM nonce for a thumbnail: the UTF-8 bytes of the size
 * name (`small`/`medium`/`large`), zero-padded on the right to 12 bytes.
 *
 * @param size - The thumbnail size used at encryption time.
 * @returns The 12-byte nonce.
 */
const buildThumbnailNonce = (size: CloudImageSize): Uint8Array => {
	const nonce = new Uint8Array(GCM_NONCE_BYTES);
	nonce.set(new TextEncoder().encode(size));
	return nonce;
};

/**
 * Decrypts an AES-256-GCM encrypted thumbnail.
 *
 * @remarks
 * `data` is the firmware's `ciphertext || tag` output (the 16-byte GCM tag is the
 * trailing part), which is exactly what Web Crypto's AES-GCM expects.
 *
 * @param data - The encrypted bytes (`ciphertext || tag`).
 * @param keyBytes - The 32-byte AES-256 recording key (full `master_key`).
 * @param size - The thumbnail size used at encryption time, e.g. `large` /
 * `small` (nonce input).
 * @returns The decrypted image bytes.
 */
export const decryptThumbnail = async (
	data: ArrayBuffer,
	keyBytes: Uint8Array,
	size: CloudImageSize,
): Promise<ArrayBuffer> => {
	const key = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'AES-GCM', false, [
		'decrypt',
	]);
	return crypto.subtle.decrypt(
		{
			name: 'AES-GCM',
			iv: buildThumbnailNonce(size) as BufferSource,
			tagLength: GCM_TAG_LENGTH_BITS,
		},
		key,
		data,
	);
};

/**
 * Fetches an encrypted thumbnail, decrypts it with the device's stored key, and
 * returns a blob object URL for display.
 *
 * @remarks
 * The returned URL is created with {@link URL.createObjectURL}; the caller is
 * responsible for revoking it once the image is no longer shown.
 *
 * @param deviceId - The device id whose key decrypts the image.
 * @param encryptedUrl - The encrypted thumbnail URL (signed before fetching).
 * @param size - The thumbnail size, e.g. `large` (nonce input for decryption).
 * @param recId - Optional recording id; enables per-recording HKDF derivation
 * (recording-bound thumbnails). Omit for device-level latest thumbnails.
 * @param recDate - Optional recording ISO `date`; resolves the era active at that
 * date (rotation-aware). Omit for device-level latest thumbnails (newest era).
 * @returns An object URL, or `null` when no key is available or decryption fails.
 */
const buildDecryptedThumbnailUrl = async (
	deviceId: string,
	encryptedUrl: string,
	size: CloudImageSize,
	recId?: string,
	recDate?: string,
): Promise<string | null> => {
	// Recording-bound images resolve the era by their own date; device-level
	// previews pass no date and get the newest era. Both reuse the era cache.
	const masterKey = await resolveMasterKey(deviceId, recDate);
	if (!masterKey) return null;

	const imageDecryptionKey = await deriveImageKey(masterKey, deviceId, recId);
	if (!imageDecryptionKey) return null;

	const signedUrl = await buildCloudMediaUrl(encryptedUrl).catch(() => null);
	if (!signedUrl) return null;

	const response = await fetch(signedUrl).catch(() => null);
	if (!response?.ok) return null;

	const encrypted = await response.arrayBuffer();
	try {
		const decrypted = await decryptThumbnail(encrypted, imageDecryptionKey, size);
		return URL.createObjectURL(new Blob([decrypted], { type: 'image/jpeg' }));
	} catch {
		console.warn('[recording-image-decryption] failed to decrypt thumbnail', {
			url: encryptedUrl.split('?')[0],
			bytes: encrypted.byteLength,
			size,
		});
		return null;
	}
};

/**
 * Resolves a displayable URL for a thumbnail, transparently handling encryption:
 * encrypted images are fetched, decrypted, and returned as a blob object URL,
 * while plain images are signed via {@link buildCloudMediaUrl}.
 *
 * @remarks
 * Object URLs (`blob:`) returned for encrypted images must be revoked by the
 * caller (see {@link revokeThumbnailUrl}) when replaced or unmounted.
 *
 * @param deviceId - The device id whose key decrypts the image.
 * @param url - The thumbnail URL (empty resolves to an empty string).
 * @param encrypted - Whether the image is encrypted.
 * @param size - The thumbnail size the `url` corresponds to (nonce input).
 * @param recId - Optional recording id; enables per-recording HKDF derivation
 * (recording-bound thumbnails). Omit for device-level latest thumbnails.
 * @param recDate - Optional recording ISO `date`; selects the rotation era active
 * at that date. Omit for device-level latest thumbnails (newest era).
 * @returns A displayable URL, or an empty string when unavailable.
 */
export const resolveThumbnailUrl = async (
	deviceId: string,
	url: string | undefined,
	encrypted: boolean,
	size: CloudImageSize,
	recId?: string,
	recDate?: string,
): Promise<string> => {
	if (!url) return '';
	if (encrypted) {
		return (await buildDecryptedThumbnailUrl(deviceId, url, size, recId, recDate)) ?? '';
	}
	return buildCloudMediaUrl(url);
};

/**
 * Revokes a blob object URL previously returned for an encrypted thumbnail.
 * Plain (non-`blob:`) URLs are ignored, so it is safe to call unconditionally.
 *
 * @param url - The URL to revoke.
 */
export const revokeThumbnailUrl = (url: string): void => {
	if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
};

/**
 * Creates a stateful thumbnail-URL resolver that a component can drive from a
 * reactive block. It centralizes the lifecycle concerns of encrypted previews:
 *
 * @remarks
 * - Accepts either an explicit `url` (recording-bound thumbnails) or a
 *   `previewSize` that reads the device's latest thumbnail from device status via
 *   {@link getCameraThumbnail} — so device previews need no per-component plumbing.
 * - Memoizes on the resolved source + the era key's availability and skips work
 *   when unchanged, so a reactive re-run with the same source does not
 *   re-fetch/re-decrypt (no flicker).
 * - Subscribes to {@link camera_storage_passphrases} (an era key preserved on
 *   unlock/provision), {@link camera_storage_resolved_keys}, and — for device
 *   previews only — that one device's thumbnail (refreshed after mount), so
 *   callers do not pass or watch the key or the thumbnail.
 * - Guards against out-of-order async results and revokes superseded blob URLs.
 * - Reports whether a resolution is in flight, so callers can tell "still
 *   fetching/decrypting" apart from "no key, nothing to show".
 *
 * @param onUrl - Callback invoked with each newly resolved displayable URL.
 * @param onLoading - Callback invoked when a resolution starts and settles.
 * @returns A handle with `update` and `destroy`.
 */
export const createThumbnailResolver = (
	onUrl: (url: string) => void,
	onLoading?: (loading: boolean) => void,
): ThumbnailResolver => {
	let currentUrl = '';
	let currentKey = '';
	let loading = false;
	let source: ThumbnailSource | null = null;
	let thumbnail: CameraThumbnailStatus | undefined;
	let boundDeviceId = '';
	let unsubscribeThumbnail: (() => void) | null = null;

	const setLoading = (next: boolean): void => {
		if (next === loading) return;
		loading = next;
		onLoading?.(next);
	};

	const run = (): void => {
		if (!source) return;
		const { deviceId, previewSize } = source;
		let { url, encrypted, recId, recDate, size = 'large' } = source;
		// Device-level preview: take the source from the bound device's thumbnail
		// (newest era).
		if (previewSize) {
			const images = thumbnail?.imageUrls;
			// Fall back to `small` when the requested size is missing.
			size = images?.[previewSize] ? previewSize : 'small';
			url = images?.[previewSize] || images?.small;
			encrypted = !!thumbnail?.encrypted;
			recId = thumbnail?.recordingId;
			recDate = undefined;
		}
		// Memoize on the era's key *availability* (not the shared device-slot key
		// value): the value ping-pongs as rotated eras resolve into the one slot,
		// while availability flips exactly once when the needed era is unlocked.
		const unlocked = encrypted && hasPreservedEraKey(deviceId, recDate) ? '1' : '';
		const key = [deviceId, url, encrypted, recId, recDate, size, unlocked].join('|');
		if (key === currentKey) return;
		currentKey = key;
		// A source-less run settles immediately; only a real source is "loading".
		setLoading(!!url);

		void resolveThumbnailUrl(deviceId, url, !!encrypted, size, recId, recDate).then((resolved) => {
			// A superseded run leaves the loading state to the newer one.
			if (key !== currentKey) {
				revokeThumbnailUrl(resolved);
				return;
			}
			setLoading(false);
			if (resolved === currentUrl) return;
			revokeThumbnailUrl(currentUrl);
			currentUrl = resolved;
			onUrl(resolved);
		});
	};

	/**
	 * Watches a single device's latest thumbnail, so a status write for any other
	 * device never reaches this resolver. An empty `deviceId` unbinds (explicit
	 * URL sources watch nothing).
	 */
	const bindThumbnail = (deviceId: string): void => {
		if (deviceId === boundDeviceId) return;
		unsubscribeThumbnail?.();
		unsubscribeThumbnail = null;
		boundDeviceId = deviceId;
		if (!deviceId) {
			thumbnail = undefined;
			return;
		}
		// The watcher fires only on later writes, so seed the current value here.
		thumbnail = getCameraThumbnail(deviceId);
		unsubscribeThumbnail = watchCameraThumbnail(deviceId, (next) => {
			thumbnail = next;
			run();
		});
	};

	const update = (next: ThumbnailSource): void => {
		source = next;
		bindThumbnail(next.previewSize ? next.deviceId : '');
		run();
	};

	// Re-resolve when an era key is preserved (unlock/provision) or the device slot
	// changes; the bound device's thumbnail is watched separately.
	const unsubscribePassphrase = camera_storage_passphrases.subscribe(run);
	const unsubscribeKeys = camera_storage_resolved_keys.subscribe(run);

	const destroy = (): void => {
		unsubscribePassphrase();
		unsubscribeKeys();
		unsubscribeThumbnail?.();
		unsubscribeThumbnail = null;
		boundDeviceId = '';
		thumbnail = undefined;
		currentKey = '';
		setLoading(false);
		revokeThumbnailUrl(currentUrl);
		currentUrl = '';
	};

	return { update, destroy };
};
